import { useEffect, useState } from 'react';
import {
  SuggestedPrompts,
  SuggestedPromptsResponse,
} from '../../models/suggested-prompts/SuggestedQuestions';
import { getSuggestedQuestions } from '../../services/suggested-prompts/SuggestedPromptsService';

// Must match the transition/animation durations in SuggestedPromptsPage.scss.
const ROW_LEAVE_MS = 320;
const ROW_ENTER_MS = 320;
const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const useSuggestedQuestions = (projectId: number) => {
  const [suggestedPromptsResponse, setSuggestedPromptsResponse] =
    useState<SuggestedPromptsResponse | null>(null);
  // true from the start: the fetch starts in an effect, after the first render — false there flashed the empty state.
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  // Row animation state for the in-place swap (classes in SuggestedPromptsPage.scss).
  const [leavingSlug, setLeavingSlug] = useState<string | null>(null);
  const [enteringSlug, setEnteringSlug] = useState<string | null>(null);

  const getQuestions = async () => {
    setLoading(true);
    try {
      const data = await getSuggestedQuestions(projectId);
      // Sorted once here, not by the table: a table sort would move the replacement row on every add.
      setSuggestedPromptsResponse({
        suggestedPrompts: [...data.suggestedPrompts].sort((a, b) => b.searchVolume - a.searchVolume),
      });
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  };

  // Drops an added prompt and fills its slot with one not yet shown. The endpoint skips added prompts
  // and returns a random sample, so a refetch is the next draw from the pool (none left → list shrinks).
  // The row is swapped in place, in one update after the fetch, so the table neither flickers nor reorders.
  const replaceQuestion = async (slug: string) => {
    const current = suggestedPromptsResponse?.suggestedPrompts ?? [];
    let next: SuggestedPrompts | undefined;
    try {
      const data = await getSuggestedQuestions(projectId);
      const shown = new Set(current.map((q) => q.question.trim().toLowerCase()));
      next = data.suggestedPrompts.find((q) => !shown.has(q.question.trim().toLowerCase()));
    } catch (err) {
      setError(err as Error);
    }
    setLeavingSlug(slug);
    await wait(ROW_LEAVE_MS);
    setSuggestedPromptsResponse({
      suggestedPrompts: current.flatMap((q) => (q.slug !== slug ? [q] : next ? [next] : [])),
    });
    setLeavingSlug(null);
    if (next) {
      const enteringSlugValue = next.slug;
      setEnteringSlug(enteringSlugValue);
      setTimeout(
        () => setEnteringSlug((s) => (s === enteringSlugValue ? null : s)),
        ROW_ENTER_MS
      );
    }
  };

  useEffect(() => {
    getQuestions();
  }, []);

  return { suggestedPromptsResponse, loading, error, replaceQuestion, leavingSlug, enteringSlug };
};

export default useSuggestedQuestions;
